import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/UI/Button';
import AddSignersModal from '@/components/DealList/ContractNew/Modal/AddSigners';
import {
  requestExternalRosterCode,
  verifyExternalRosterCode,
  operateExternalRoster,
  ExternalRoster,
} from '@/api/signatureV2.api';
import { SignatureV2ParticipantInput } from '@/types/signatureV2';
import { Page, PublicContent, Notice, Field, Input, Actions } from './styles';
import { RosterCard, RosterList, VerificationForm } from './externalRosterStyles';

export default function ExternalRosterPage() {
  const { token = '' } = useParams();
  const activeToken = useRef(token);
  activeToken.current = token;
  // @ts-ignore legacy locale key depth
  const { t: typedT } = useTranslation();
  const t = typedT as (key: string, options?: any) => string;
  const [roster, setRoster] = useState<ExternalRoster | null>(null);
  const [grant, setGrant] = useState('');
  const [challenge, setChallenge] = useState('');
  const [code, setCode] = useState('');
  const [availableAt, setAvailableAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const [error, setError] = useState('');
  const [edit, setEdit] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    setRoster(null);
    setGrant('');
    setChallenge('');
    setCode('');
    setDone(false);
    setError('');
    setEdit(false);
    setAvailableAt(0);
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [token]);
  const remaining = Math.max(0, Math.ceil((availableAt - now) / 1000));
  const run = async (operation: () => Promise<void>) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError('');
    try {
      await operation();
    } catch (failure: any) {
      if (activeToken.current !== token) return;
      setError(failure?.response?.data?.message || t('signatureV2.errors.generic'));
      if ([403, 410].includes(failure?.response?.status)) {
        setGrant('');
        setRoster(null);
        setEdit(false);
      }
      if (failure?.response?.status === 429)
        setAvailableAt(Date.now() + Number(failure.response.headers?.['retry-after'] || 60) * 1000);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const save = (signers: SignatureV2ParticipantInput[]) =>
    run(async () => {
      const response = await operateExternalRoster(token, grant, 'save', {
        expected_version: roster!.version,
        signers,
      });
      if (activeToken.current !== token) return;
      setRoster(response.data.data);
      setEdit(false);
    });
  return (
    <Page>
      <Helmet>
        <meta name="referrer" content="no-referrer" />
      </Helmet>
      <PublicContent>
        <RosterCard>
          <h1>{t('signatureV2.externalRoster.title')}</h1>
          {error && (
            <Notice danger role="alert">
              {error}
            </Notice>
          )}
          {done ? (
            <p role="status">{t('signatureV2.externalRoster.confirmed')}</p>
          ) : !grant ? (
            <>
              <p>{t('signatureV2.externalRoster.notice')}</p>
              <Button
                variant="primary-invert"
                type="button"
                disabled={busy || remaining > 0 || token.length !== 64}
                onClick={() =>
                  run(async () => {
                    const response = await requestExternalRosterCode(token);
                    if (activeToken.current !== token) return;
                    setChallenge(response.data.data.challenge_id);
                    setAvailableAt(Date.parse(response.data.data.resend_available_at));
                  })
                }
              >
                {remaining
                  ? t('signatureV2.externalRoster.cooldown', { seconds: remaining })
                  : t('signatureV2.externalRoster.sendCode')}
              </Button>
              {challenge && (
                <VerificationForm
                  onSubmit={(event) => {
                    event.preventDefault();
                    void run(async () => {
                      const response = await verifyExternalRosterCode(token, challenge, code);
                      if (activeToken.current !== token) return;
                      setGrant(response.data.data.grant);
                      setRoster(response.data.data.roster);
                      setCode('');
                    });
                  }}
                >
                  <Field>
                    {t('signatureV2.externalRoster.code')}
                    <Input
                      name="verification-code"
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      required
                      maxLength={6}
                      value={code}
                      onChange={(event) => setCode(event.target.value)}
                    />
                  </Field>
                  <Button variant="primary" type="submit" disabled={busy}>
                    {t('signatureV2.externalRoster.verify')}
                  </Button>
                </VerificationForm>
              )}
            </>
          ) : (
            roster && (
              <>
                <h2>{roster.document_name}</h2>
                <RosterList>
                  {roster.signers.map((signer, i) => (
                    <li key={i}>
                      {signer.name} — {signer.email}
                    </li>
                  ))}
                </RosterList>
                <Actions>
                  <Button
                    variant="primary-invert"
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        const response = await operateExternalRoster(token, grant, 'view');
                        if (activeToken.current === token) setRoster(response.data.data);
                      })
                    }
                  >
                    {t('signatureV2.externalRoster.refresh')}
                  </Button>
                  <Button variant="primary-invert" type="button" disabled={busy} onClick={() => setEdit(true)}>
                    {t('signatureV2.externalRoster.edit')}
                  </Button>
                  <Button
                    variant="primary"
                    type="button"
                    disabled={busy || !roster.signers.length}
                    onClick={() =>
                      run(async () => {
                        await operateExternalRoster(token, grant, 'confirm', { expected_version: roster.version });
                        if (activeToken.current !== token) return;
                        setDone(true);
                        setGrant('');
                        setRoster(null);
                      })
                    }
                  >
                    {t('signatureV2.externalRoster.confirm')}
                  </Button>
                </Actions>
                <AddSignersModal
                  showModal={edit}
                  side="buyer"
                  companyName={t('signatureV2.side.buyer')}
                  candidates={[]}
                  initialSigners={roster.signers}
                  otherPartyEmails={[]}
                  busy={busy}
                  apiError={error}
                  setShowModal={setEdit}
                  onSubmit={save}
                />
              </>
            )
          )}
        </RosterCard>
      </PublicContent>
    </Page>
  );
}
